import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import { createVitalSigns } from "../services/vitalSignsService";
import { useAuth } from "../context/AuthContext";
import {
  DENSE_FIELD_PROPS,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../utils/formModalStyles";

const EMPTY_VITALS = {
  bpReading: "",
  weightReading: "",
  heightReading: "",
  oxygenSaturation: "",
  pulseRate: "",
  respirationRate: "",
};

const isNumericOrEmpty = (value) => value === "" || /^\d*\.?\d*$/.test(value);
// BP allows systolic/diastolic form while typing, e.g. 120/80
const isBpReadingOrEmpty = (value) =>
  value === "" || /^\d*\.?\d*(\/\d*\.?\d*)?$/.test(value);

/**
 * Optional front-desk vital signs capture after registration / consultation invoice.
 */
const FrontDeskVitalSignsPanel = forwardRef(function FrontDeskVitalSignsPanel(
  {
    patientId,
    appointmentId,
    enabled = true,
    hidePromptButtons = false,
    hideFormActions = false,
    compact = false,
    mode: controlledMode,
    onModeChange,
    onSaveStateChange,
    onSaved,
  },
  ref
) {
  const { user } = useAuth();
  const [internalMode, setInternalMode] = useState("prompt");
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(EMPTY_VITALS);

  const mode = controlledMode !== undefined ? controlledMode : internalMode;
  const setMode = (nextMode) => {
    if (onModeChange) onModeChange(nextMode);
    if (controlledMode === undefined) setInternalMode(nextMode);
  };

  const canSave = useMemo(() => {
    if (!patientId) return false;
    return Object.values(values).some((v) => String(v || "").trim() !== "");
  }, [patientId, values]);

  useEffect(() => {
    onSaveStateChange?.({ saving, canSave });
  }, [saving, canSave, onSaveStateChange]);

  const setField = (name, value) => {
    const isValid =
      name === "bpReading" ? isBpReadingOrEmpty(value) : isNumericOrEmpty(value);
    if (!isValid) return;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setValues(EMPTY_VITALS);
    setMode("prompt");
  };

  const handleSave = async () => {
    if (!patientId) {
      toast.error("Patient is required to save vital signs");
      return;
    }
    if (!canSave) {
      toast.info("Enter at least one vital reading, or skip");
      return;
    }

    setSaving(true);
    try {
      const saved = await createVitalSigns({
        patientId,
        appointmentId: appointmentId || null,
        prescriptionId: null,
        ...values,
        source: "FRONT_DESK",
        recordedByUserId: user?.id || null,
      });

      if (saved?.cachedOnly) {
        toast.success(
          "Vital signs saved locally for this visit. Backend create API not available yet — doctor can still load them in this browser session."
        );
      } else {
        toast.success("Vital signs saved. Doctor can use them when writing the prescription.");
      }
      setMode("saved");
      onSaved?.();
    } catch (error) {
      console.error("[FrontDeskVitalSignsPanel] save failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save vital signs"
      );
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    resetForm,
    canSave,
    saving,
  }));

  if (!enabled) return null;

  const boxMt = compact ? 0.5 : 2;
  const boxMb = compact ? 0.5 : 1;

  return (
    <Box mt={boxMt} mb={boxMb} width="100%">
      {mode === "prompt" && (
        <Box display="flex" flexDirection="column" gap={compact ? 0.75 : 1.5}>
          {!hidePromptButtons && (
            <Box display="flex" gap={1}>
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                sx={FORM_SUBMIT_BUTTON_SX}
                onClick={() => setMode("form")}
                disabled={!patientId}
              >
                Add Vital Signs
              </Button>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                fullWidth
                sx={FORM_CANCEL_BUTTON_SX}
                onClick={() => setMode("saved")}
              >
                Skip
              </Button>
            </Box>
          )}
        </Box>
      )}

      {mode === "form" && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
            Vital Signs
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="BP Reading"
                value={values.bpReading}
                onChange={(e) => setField("bpReading", e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pulse Rate"
                value={values.pulseRate}
                onChange={(e) => setField("pulseRate", e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
                }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weight"
                value={values.weightReading}
                onChange={(e) => setField("weightReading", e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Height"
                value={values.heightReading}
                onChange={(e) => setField("heightReading", e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SpO2"
                value={values.oxygenSaturation}
                onChange={(e) => setField("oxygenSaturation", e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Respiration Rate"
                value={values.respirationRate}
                onChange={(e) => setField("respirationRate", e.target.value)}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            {!hideFormActions && (
              <Grid item xs={12} display="flex" gap={1}>
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  fullWidth
                  sx={FORM_CANCEL_BUTTON_SX}
                  disabled={saving}
                  onClick={resetForm}
                >
                  Back
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  fullWidth
                  sx={FORM_CANCEL_BUTTON_SX}
                  disabled={saving}
                  onClick={() => setMode("saved")}
                >
                  Skip
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  fullWidth
                  sx={FORM_SUBMIT_BUTTON_SX}
                  disabled={saving || !canSave}
                  onClick={handleSave}
                >
                  {saving ? <CircularProgress size={16} color="inherit" /> : "Save Vital Signs"}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {mode === "saved" && (
        <Alert severity="success" sx={{ mb: 0.5, py: 0.25, "& .MuiAlert-message": { py: 0.25 } }}>
          Vital signs step skipped or saved. You may close or register another patient.
        </Alert>
      )}
    </Box>
  );
});

export default FrontDeskVitalSignsPanel;
